import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import {
  useArea,
  useDistrict,
  useDivision,
  useThana,
} from '../../rest/useAddress';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ICompanyDetails } from '../../rest/types';

type IProps = {
  division_id: number;
  district_id: number;
  thana_id: number;
  area_id: number;
};

type IStateProps = {
  setAreaID: Dispatch<SetStateAction<string>>;
  companyData?: ICompanyDetails | any;
  isRequired?: boolean;
};

function AddressSelect({
  setAreaID,
  companyData,
  isRequired = false,
}: IStateProps) {
  const [addressID, setAddressID] = useState<IProps>({
    division_id: companyData?.division_id ?? 0,
    district_id: companyData?.district_id ?? 0,
    thana_id: companyData?.thana_id ?? 0,
    area_id: companyData?.area_id ?? 0,
  });

  useEffect(() => {
    if (companyData) {
      setAddressID({
        division_id: companyData.division_id ?? 0,
        district_id: companyData.district_id ?? 0,
        thana_id: companyData.thana_id ?? 0,
        area_id: companyData.area_id ?? 0,
      });
      if (companyData.area_id) {
        setAreaID(String(companyData.area_id));
      }
    }
  }, [companyData, setAreaID]);

  const { division, isLoading } = useDivision();
  const { district, isLoading: districtLoading } = useDistrict({
    division_id: addressID.division_id,
  });
  const { thana, isLoading: thanaLoading } = useThana({
    division_id: addressID.division_id,
    district_id: addressID.district_id,
  });
  const { area, isLoading: areaLoading } = useArea({
    division_id: addressID.division_id,
    district_id: addressID.district_id,
    thana_id: addressID.thana_id,
  });

  return (
    <>
      <Grid item xs={12} sm={6} md={6} lg={4}>
        <FormControl fullWidth>
          <InputLabel id='division-select-label'>Select division</InputLabel>
          <Select
            required={isRequired}
            labelId='division-select-label'
            id='division-select'
            label='Select division'
            value={addressID.division_id ? String(addressID.division_id) : ''}
            onChange={(e) => {
              setAddressID({
                division_id: Number(e.target.value),
                district_id: 0,
                thana_id: 0,
                area_id: 0,
              });
              setAreaID('');
            }}
          >
            {isLoading && <MenuItem value=''>Loading...</MenuItem>}
            {!isLoading && division.length === 0 && (
              <MenuItem value=''>Not found</MenuItem>
            )}
            {division.length > 0 &&
              division.map((item, i) => (
                <MenuItem key={i + 1} value={String(item.division_id)}>
                  {item.division_name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={4}>
        <FormControl fullWidth>
          <InputLabel id='district-select-label'>Select district</InputLabel>
          <Select
            required={isRequired}
            labelId='district-select-label'
            id='district-select'
            label='Select district'
            value={addressID.district_id ? String(addressID.district_id) : ''}
            onChange={(e) => {
              setAddressID((prev) => ({
                ...prev,
                district_id: Number(e.target.value),
                thana_id: 0,
                area_id: 0,
              }));
              setAreaID('');
            }}
          >
            {districtLoading && <MenuItem value=''>Loading...</MenuItem>}
            {!isLoading && district.length === 0 && (
              <MenuItem value=''>Not found</MenuItem>
            )}
            {district.length > 0 &&
              district.map((item, i) => (
                <MenuItem key={i + 1} value={String(item.district_id)}>
                  {item.district_name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={4}>
        <FormControl fullWidth>
          <InputLabel id='thana-select-label'>Select thana</InputLabel>
          <Select
            required={isRequired}
            labelId='thana-select-label'
            id='thana-select'
            label='Select thana'
            value={addressID.thana_id ? String(addressID.thana_id) : ''}
            onChange={(e) => {
              setAddressID((prev) => ({
                ...prev,
                thana_id: Number(e.target.value),
                area_id: 0,
              }));
              setAreaID('');
            }}
          >
            {thanaLoading && <MenuItem value=''>Loading...</MenuItem>}
            {!isLoading && thana.length === 0 && <MenuItem value=''>Not found</MenuItem>}
            {thana.length > 0 &&
              thana.map((item, i) => (
                <MenuItem key={i + 1} value={String(item.thana_id)}>
                  {item.thana_name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6} md={6} lg={4}>
        <FormControl fullWidth>
          <InputLabel id='area-select-label'>Select area</InputLabel>
          <Select
            required={isRequired}
            labelId='area-select-label'
            id='area-select'
            label='Select area'
            value={addressID.area_id ? String(addressID.area_id) : ''}
            onChange={(e: SelectChangeEvent<string>) => {
              const val = e.target.value;
              setAddressID((prev) => ({
                ...prev,
                area_id: Number(val),
              }));
              setAreaID(val);
            }}
          >
            {areaLoading && <MenuItem value=''>Loading...</MenuItem>}
            {!isLoading && area.length === 0 && <MenuItem value=''>Not found</MenuItem>}
            {area.length > 0 &&
              area.map((item, i) => (
                <MenuItem key={i + 1} value={String(item.area_id)}>
                  {item.area_name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
    </>
  );
}

export default AddressSelect;

